import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { api, resolveImage, formatApiErrorDetail } from "@/lib/api";
import ImageUpload from "@/components/admin/ImageUpload";

const EMPTY = { name: "", description: "", ingredients: "", price: "", size: "", image_url: "", available: true, sort_order: 0 };

const field = "w-full rounded-xl border border-[#1A1A1A]/15 px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#FF7A1A]/40 bg-white";

const JuicesManager = () => {
  const [juices, setJuices] = useState([]);
  const [editing, setEditing] = useState(null); // null=closed, {}=form data
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/juices").then((r) => setJuices(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...EMPTY, sort_order: juices.length + 1 });
  const openEdit = (j) => setEditing({ ...j });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...editing, sort_order: Number(editing.sort_order) || 0 };
    delete payload.id;
    delete payload.created_at;
    try {
      if (editing.id) {
        await api.put(`/juices/${editing.id}`, payload);
        toast.success("Juice updated");
      } else {
        await api.post("/juices", payload);
        toast.success("Juice added");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this juice?")) return;
    await api.delete(`/juices/${id}`);
    toast.success("Juice deleted");
    load();
  };

  return (
    <div data-testid="juices-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-extrabold text-2xl text-[#1A1A1A]">Juices ({juices.length})</h2>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-[#FF7A1A] hover:bg-[#FFC93C] hover:text-[#1A1A1A] text-white font-bold text-sm px-5 py-2.5 transition-colors" data-testid="add-juice-btn">
          <Plus className="w-4 h-4" /> Add Juice
        </button>
      </div>

      <div className="grid gap-3">
        {juices.map((j) => (
          <div key={j.id} className="flex items-center gap-4 bg-white rounded-2xl border border-[#1A1A1A]/5 p-3 shadow-sm" data-testid={`juice-row-${j.id}`}>
            <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#FFF8F0] flex-shrink-0">
              {j.image_url && <img src={resolveImage(j.image_url)} alt={j.name} className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-display font-bold text-[#1A1A1A] truncate">{j.name}</p>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${j.available ? "bg-[#7ED321]/15 text-[#5A9E12]" : "bg-[#FF3D68]/15 text-[#FF3D68]"}`}>
                  {j.available ? "Available" : "Sold Out"}
                </span>
              </div>
              <p className="text-sm text-[#1A1A1A]/50 truncate">{j.price} · {j.size}</p>
            </div>
            <button onClick={() => openEdit(j)} className="p-2 rounded-lg hover:bg-[#FFF8F0] text-[#1A1A1A]/60" data-testid={`edit-juice-${j.id}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(j.id)} className="p-2 rounded-lg hover:bg-[#FF3D68]/10 text-[#FF3D68]" data-testid={`delete-juice-${j.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" data-testid="juice-form-modal">
          <div className="absolute inset-0 bg-[#1A1A1A]/50 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <form onSubmit={save} className="relative bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 sm:p-8 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-extrabold text-xl">{editing.id ? "Edit Juice" : "New Juice"}</h3>
              <button type="button" onClick={() => setEditing(null)} className="p-1.5 rounded-full hover:bg-[#FFF8F0]"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-[#1A1A1A]/70">Photo</label>
                <div className="mt-2"><ImageUpload value={editing.image_url} onChange={(url) => setEditing((p) => ({ ...p, image_url: url }))} testId="juice-image" /></div>
              </div>
              <div>
                <label className="text-sm font-semibold text-[#1A1A1A]/70">Name</label>
                <input className={field} value={editing.name} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} required data-testid="juice-name-input" />
              </div>
              <div>
                <label className="text-sm font-semibold text-[#1A1A1A]/70">Short description</label>
                <textarea className={field} rows={2} value={editing.description} onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))} data-testid="juice-description-input" />
              </div>
              <div>
                <label className="text-sm font-semibold text-[#1A1A1A]/70">Ingredients (comma separated)</label>
                <input className={field} value={editing.ingredients} onChange={(e) => setEditing((p) => ({ ...p, ingredients: e.target.value }))} data-testid="juice-ingredients-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-[#1A1A1A]/70">Price</label>
                  <input className={field} value={editing.price} onChange={(e) => setEditing((p) => ({ ...p, price: e.target.value }))} placeholder="$8.50" data-testid="juice-price-input" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#1A1A1A]/70">Size</label>
                  <input className={field} value={editing.size} onChange={(e) => setEditing((p) => ({ ...p, size: e.target.value }))} placeholder="16 oz" data-testid="juice-size-input" />
                </div>
              </div>
              <div className="flex items-center justify-between bg-[#FFF8F0] rounded-xl px-4 py-3">
                <span className="text-sm font-semibold text-[#1A1A1A]/70">Available</span>
                <button type="button" onClick={() => setEditing((p) => ({ ...p, available: !p.available }))} className={`w-12 h-7 rounded-full transition-colors relative ${editing.available ? "bg-[#7ED321]" : "bg-[#1A1A1A]/20"}`} data-testid="juice-available-toggle">
                  <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${editing.available ? "left-6" : "left-1"}`} />
                </button>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setEditing(null)} className="flex-1 rounded-full border border-[#1A1A1A]/15 font-bold py-3 hover:bg-[#FFF8F0]">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 rounded-full bg-[#FF7A1A] hover:bg-[#FFC93C] hover:text-[#1A1A1A] text-white font-bold py-3 transition-colors disabled:opacity-60" data-testid="save-juice-btn">
                {saving ? "Saving…" : "Save Juice"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default JuicesManager;
